import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { stringify } from 'query-string';
import { DetailsDrawer, useClient, useMutation, useQuery, useQueryClient } from 'shared-ui';

import { fetchDodDlq, fetchDodDlqSummary, fetchDodTimeseries, purgeDodDlq, retryDodDlq } from 'api/dod/dod.v2';
import { DodDlqBulkRequest, DodDlqEntry, DodTimeseriesLabelName, DodTimeseriesSeries } from 'api/dod/dod.v2.types';
import { Functionality } from 'api/userPermissions/userPermissions.enum';
import { QueryKeyEnum } from 'core/global.enum';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { BarDatum } from '../components/barPanel/BarPanel.types';
import { rangeStepSeconds, useDodFilters } from '../hooks/useDodFilters/useDodFilters';
import { useDodOutlet } from '../hooks/useDodOutlet/useDodOutlet';

import { DlqDetails } from './DlqDetails';
import { DlqTab } from './DlqTab';
import { DlqConfirm } from './DlqTab.types';
import {
  DLQ_CSV_MAX_ROWS,
  DLQ_PAGE_SIZES,
  DlqErrorClass,
  DlqReason,
  DlqRow,
  buildDlqCsv,
  collectDlqPages,
  toDlqQueryFilter,
  toDlqRow,
} from './dlq.helpers';

const SEARCH_DEBOUNCE_MS = 300;

/** Rollup metric behind the "moves to dead letter" panel (design §3.1). */
const DLQ_MOVED_METRIC = 'dod_dlq_moved_total';

/**
 * `dim` is the rollups' generic discriminator, filled from the first of reason/state/result/kind/
 * outcome/queue present on the recorded labels. For dod_dlq_moved_total that is the DLQ reason, so
 * the panel stacks exactly as the artboard shows.
 *
 * On a backend that predates `dim` the response comes back without those labels; the panel then
 * falls back to labelling each series by its key and says it is not split by reason, rather than
 * silently drawing one anonymous band per reason.
 */
const DLQ_MOVED_GROUP_BY: DodTimeseriesLabelName[] = ['dim'];

const DLQ_REASON_VALUES = ['permanent', 'max-retries', 'unknown-cap'];

const downloadCsv = (csv: string, fileName: string): void => {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const DlqTabContainer = (): JSX.Element => {
  const { formatMessage } = useLocale();
  const queryClient = useQueryClient();
  const { axios: client } = useClient();
  const { perms } = usePermissions();
  const { capabilities, refreshToken, setToolbarOptions, setDetails } = useDodOutlet();
  const { range, refreshMs, filters, resolveRange } = useDodFilters();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DLQ_PAGE_SIZES[0]);
  const [reason, setReason] = useState<DlqReason | ''>('');
  const [errorClass, setErrorClass] = useState<DlqErrorClass | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedErrorMessage, setSelectedErrorMessage] = useState('');
  const [checkedRows, setCheckedRows] = useState<DlqRow[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<DlqConfirm | null>(null);
  const [isExportingFilter, setIsExportingFilter] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  /**
   * The window is resolved once per render pass rather than read from `fromTs`/`toTs`, which slide
   * with `Date.now()` on every render and would make the query key change continuously.
   */
  const queryFilter = useMemo(
    () => {
      const { fromTs, toTs } = resolveRange();

      return toDlqQueryFilter({
        shared: { rcp: filters.rcp, nlk: filters.nlk, cmd: filters.cmd },
        fromTs,
        toTs,
        reason,
        errorClass,
        search,
      });
    },
    // `range` stands in for the sliding window: a preset change is what should refetch, not a tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.rcp, filters.nlk, filters.cmd, range, reason, errorClass, search, refreshToken, resolveRange],
  );

  // Resolved on every render (each poll re-renders), so a preset window ends now, not at mount.
  const { fromTs: liveFrom, toTs: liveTo } = resolveRange();

  const {
    data: dlqPage,
    isLoading,
    isError,
  } = useQuery([QueryKeyEnum.DOD_DLQ, queryFilter, page, pageSize], fetchDodDlq, {
    handleErrorManually: true,
    args: { ...queryFilter, from: liveFrom, to: liveTo, page: page + 1, pageSize },
    refetchInterval: refreshMs,
    keepPreviousData: true,
  });

  const { data: summary, isLoading: isSummaryLoading } = useQuery(
    [QueryKeyEnum.DOD_DLQ_SUMMARY, range, filters.rcp, filters.nlk],
    fetchDodDlqSummary,
    {
      handleErrorManually: true,
      args: {
        range: range === 'custom' ? '24h' : range,
        ...(filters.rcp ? { rcpId: filters.rcp } : {}),
        ...(filters.nlk ? { nlk: Number(filters.nlk) } : {}),
      },
      refetchInterval: refreshMs,
    },
  );

  const { data: moved, isLoading: isMovedLoading } = useQuery(
    [QueryKeyEnum.DOD_TIMESERIES, DLQ_MOVED_METRIC, range, filters.rcp, filters.nlk, refreshToken],
    fetchDodTimeseries,
    {
      handleErrorManually: true,
      args: {
        metric: DLQ_MOVED_METRIC,
        from: resolveRange().fromTs,
        to: resolveRange().toTs,
        step: rangeStepSeconds(range) * 1000,
        groupBy: DLQ_MOVED_GROUP_BY,
        ...(filters.rcp ? { rcpId: filters.rcp } : {}),
        ...(filters.nlk ? { nlk: filters.nlk } : {}),
      },
      enabled: capabilities.timeseries,
      refetchInterval: refreshMs,
    },
  );

  const invalidate = useCallback((): void => {
    queryClient.invalidateQueries([QueryKeyEnum.DOD_DLQ]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_DLQ_SUMMARY]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_QUEUES]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_STATUS]);
  }, [queryClient]);

  const { mutate: mutateRetry, isLoading: isRetrying } = useMutation(retryDodDlq, {
    onSuccess: (data) => {
      toast.dark(formatMessage({ id: AppMessages['dod.dlq.retry.success'] }, { count: data.retried }));
      setCheckedRows([]);
      invalidate();
    },
    onError: () => toast.dark(formatMessage({ id: AppMessages['dod.dlq.retry.error'] })),
  });

  const { mutate: mutatePurge, isLoading: isPurging } = useMutation(purgeDodDlq, {
    onSuccess: (data) => {
      toast.dark(formatMessage({ id: AppMessages['dod.dlq.purge.success'] }, { count: data.purged }));
      setCheckedRows([]);
      setSelectedRowId(null);
      invalidate();
    },
    onError: () => toast.dark(formatMessage({ id: AppMessages['dod.dlq.purge.error'] })),
  });

  const rows = useMemo(() => (dlqPage?.items ?? []).map((entry: DodDlqEntry) => toDlqRow(entry)), [dlqPage]);

  const byReason = useMemo<BarDatum[]>(
    () =>
      (summary?.byReason ?? []).map((bucket) => ({
        id: bucket.reason,
        label: formatMessage({ id: AppMessages[`dod.common.state.${bucket.reason}`] }),
        value: bucket.count,
      })),
    [summary, formatMessage],
  );

  const topErrors = useMemo<BarDatum[]>(
    () =>
      (summary?.topErrors ?? []).map((bucket) => ({ id: bucket.message, label: bucket.message, value: bucket.count })),
    [summary],
  );

  const movedSeries = useMemo<DodTimeseriesSeries[]>(() => moved?.series ?? [], [moved]);

  const hasReasonLabels = useMemo(() => movedSeries.some((series) => !!series.labels.dim), [movedSeries]);

  const movedSeriesLabels = useMemo(
    () =>
      movedSeries.reduce<Record<string, string>>((labels, series) => {
        const dim = series.labels.dim;

        if (!dim) {
          return { ...labels, [series.key]: series.key };
        }

        return {
          ...labels,
          [series.key]: DLQ_REASON_VALUES.includes(dim)
            ? formatMessage({ id: AppMessages[`dod.common.state.${dim as 'permanent'}`] })
            : dim,
        };
      }, {}),
    [movedSeries, formatMessage],
  );

  const movedTotal = useMemo(
    () =>
      movedSeries.reduce((sum, series) => sum + series.points.reduce((inner, [, value]) => inner + (value ?? 0), 0), 0),
    [movedSeries],
  );

  const total = dlqPage?.total ?? 0;

  /**
   * The toolbar's RCP and TKD selects are filled by whichever tab is mounted. Options come from the
   * rows actually on screen, so the lists can only ever offer a filter that matches something.
   */
  useEffect(() => {
    const byRcp = new Map(rows.map((row) => [row.rcpId, row.rcpLabel]));
    const byTkd = new Map(rows.map((row) => [String(row.nlk), row.tkdLabel]));

    setToolbarOptions({
      rcp: Array.from(byRcp, ([value, label]) => ({ value, label })),
      tkd: Array.from(byTkd, ([value, label]) => ({ value, label })),
    });
  }, [rows, setToolbarOptions]);

  /** The open row is held by id, so a refetch keeps the panel on the same order, not on a stale copy. */
  const selectedRow = useMemo(() => rows.find((row) => row.id === selectedRowId) ?? null, [rows, selectedRowId]);

  const onSelectRow = useCallback((row: DlqRow) => setSelectedRowId(row.id), []);

  const onCloseDetails = useCallback(() => setSelectedRowId(null), []);

  const onSelectTopError = useCallback((message: string) => {
    setSelectedErrorMessage((previous) => (previous === message ? '' : message));
    setSearchInput((previous) => (previous === message ? '' : message));
    setPage(0);
  }, []);

  const onConfirm = useCallback(() => {
    if (!confirm) {
      return;
    }

    // A single-row action from the details panel carries its own id; the bulk ones use the selection.
    const ids = confirm.ids ?? checkedRows.map((row) => row.id);
    const request: DodDlqBulkRequest = confirm.action === 'retryByFilter' ? { filter: queryFilter } : { ids };

    if (confirm.action === 'purgeSelected' || confirm.action === 'purgeRow') {
      mutatePurge(request);
    } else {
      mutateRetry(request);
    }

    setConfirm(null);
  }, [confirm, checkedRows, queryFilter, mutatePurge, mutateRetry]);

  const onExportCsv = useCallback(() => {
    downloadCsv(buildDlqCsv(rows), `dod-dlq-${new Date().toISOString().slice(0, 19)}.csv`);
  }, [rows]);

  /**
   * Export everything the current filter matches, not just the page on screen.
   *
   * The backend caps `pageSize` at 200, so the whole filter is walked page by page. The axios
   * client is used directly rather than react-query because this is a one-off imperative walk
   * whose pages must not be cached: a stale page here would silently ship the operator a mixture
   * of two different points in time.
   */
  const onExportByFilter = useCallback(async () => {
    setIsExportingFilter(true);

    try {
      const { rows: allRows, truncated } = await collectDlqPages((page_: number, pageSize_: number) =>
        client.get(`/dod/dlq?${stringify({ ...queryFilter, page: page_, pageSize: pageSize_ })}`),
      );

      downloadCsv(buildDlqCsv(allRows), `dod-dlq-filter-${new Date().toISOString().slice(0, 19)}.csv`);

      if (truncated) {
        toast.dark(formatMessage({ id: AppMessages['dod.dlq.exportTruncated'] }, { count: DLQ_CSV_MAX_ROWS }));
      }
    } catch {
      toast.dark(formatMessage({ id: AppMessages['dod.common.error'] }));
    } finally {
      setIsExportingFilter(false);
    }
  }, [client, queryFilter, formatMessage]);

  /**
   * The backend borrows `Functionality.UZYTKOWNICY` for the whole /dod surface (design §1), so the
   * same functionality decides whether the write buttons are shown. The buttons are hidden only
   * when we positively know the user lacks edit: the route itself is already admin-gated, and a
   * permissions payload that has not arrived must not blank an administrator's toolbar.
   */
  const canModify = perms?.[Functionality.UZYTKOWNICY]?.edit !== false;

  const isMutating = isRetrying || isPurging;

  const onRetryRow = useCallback((id: string) => mutateRetry({ ids: [id] }), [mutateRetry]);

  const onPurgeRow = useCallback((id: string) => setConfirm({ action: 'purgeRow', count: 1, ids: [id] }), []);

  /**
   * The details panel is the page's, not the tab's: the shell renders it through PageLayout's
   * `renderDetails`, so it sits beside the table like the card, event and reader panels.
   *
   * Every handler in the dependency list is memoised. An inline arrow here would make this effect
   * run on every render, and `setDetails` writing shell state on every render is a render loop.
   */
  useEffect(() => {
    setDetails(
      selectedRow ? (
        <DetailsDrawer isOpen header={formatMessage({ id: AppMessages['dod.dlq.details.title'] })}>
          <DlqDetails
            key={selectedRow.id}
            row={selectedRow}
            canModify={canModify}
            isMutating={isMutating}
            onRetry={onRetryRow}
            onPurge={onPurgeRow}
            onClose={onCloseDetails}
          />
        </DetailsDrawer>
      ) : null,
    );
  }, [setDetails, formatMessage, selectedRow, canModify, isMutating, onRetryRow, onPurgeRow, onCloseDetails]);

  useEffect(() => () => setDetails(null), [setDetails]);

  return (
    <DlqTab
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      total={total}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(0);
      }}
      byReason={byReason}
      topErrors={topErrors}
      isSummaryLoading={isSummaryLoading}
      movedSeries={movedSeries}
      movedSeriesLabels={movedSeriesLabels}
      movedTotal={movedTotal}
      isMovedLoading={isMovedLoading}
      hasTimeseries={capabilities.timeseries && (moved?.capability ?? true)}
      hasReasonLabels={hasReasonLabels}
      reason={reason}
      errorClass={errorClass}
      search={searchInput}
      selectedErrorMessage={selectedErrorMessage}
      onReasonChange={(next) => {
        setReason(next);
        setPage(0);
      }}
      onErrorClassChange={(next) => {
        setErrorClass(next);
        setPage(0);
      }}
      onSearchChange={(next) => {
        setSearchInput(next);
        setPage(0);
      }}
      onSelectTopError={onSelectTopError}
      checkedRows={checkedRows}
      onCheckedChange={setCheckedRows}
      selectedId={selectedRowId ?? undefined}
      onSelectRow={onSelectRow}
      onRetrySelected={() => setConfirm({ action: 'retrySelected', count: checkedRows.length })}
      onRetryByFilter={() => setConfirm({ action: 'retryByFilter', count: total })}
      onPurgeSelected={() => setConfirm({ action: 'purgeSelected', count: checkedRows.length })}
      onRetryRow={onRetryRow}
      onExportCsv={onExportCsv}
      onExportByFilter={onExportByFilter}
      isExportingFilter={isExportingFilter}
      isMutating={isMutating}
      canModify={canModify}
      confirm={confirm}
      onConfirm={onConfirm}
      onCancelConfirm={() => setConfirm(null)}
    />
  );
};
